import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import {
  Package, CheckCircle, CreditCard, XCircle, Clock, Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const FREQ_LABELS = {
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  semester: 'Semestral',
  annual: 'Anual',
  one_time: 'Pago Único'
};

// Sorts children by grade order (Pre-K/K first, then 1ro..12mo numerically)
const gradeSortValue = (grade) => {
  const g = (grade || '').toLowerCase();
  if (g.startsWith('pre')) return -2;
  if (g.startsWith('k')) return -1;
  const match = g.match(/\d+/);
  return match ? parseInt(match[0], 10) : 999;
};
const sortChildrenByGrade = (list) => [...list].sort((a, b) => {
  const diff = gradeSortValue(a.assigned_grade) - gradeSortValue(b.assigned_grade);
  if (diff !== 0) return diff;
  return (a.assigned_grade || '').localeCompare(b.assigned_grade || '');
});

const ParentPackages = ({ children = [] }) => {
  const sortedChildren = sortChildrenByGrade(children);
  const [packages, setPackages] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [subscribeData, setSubscribeData] = useState({
    student_id: '', family_wide: false
  });

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/packages/active');
      setPackages(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await axios.get('/packages/my-subscriptions');
      setSubscriptions(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchPackages(); fetchSubscriptions(); }, [fetchPackages, fetchSubscriptions]);

  // Handle Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const subId = params.get('sub_id');
    
    if (paymentStatus === 'success' && subId) {
      // Confirm payment on backend
      axios.post(`/packages/confirm-payment/${subId}`)
        .then(res => {
          toast.success(`Pago confirmado. Recibo: ${res.data.receipt_number}`);
          fetchSubscriptions();
        })
        .catch(() => {
          toast.info('Pago registrado. Se procesará pronto.');
          fetchSubscriptions();
        });
      // Clean URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancel') {
      toast.info('Pago cancelado.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchSubscriptions]);

  const openSubscribe = (pkg) => {
    setSelectedPackage(pkg);
    setSubscribeData({ student_id: children.length === 1 ? children[0].id : '', family_wide: false });
    setShowSubscribeDialog(true);
  };

  const handleSubscribe = async () => {
    if (!subscribeData.family_wide && !subscribeData.student_id) {
      toast.error('Seleccione un estudiante o elija paquete familiar');
      return;
    }
    try {
      const res = await axios.post('/packages/subscribe', {
        package_id: selectedPackage.id,
        student_id: subscribeData.family_wide ? null : subscribeData.student_id,
        family_wide: subscribeData.family_wide,
        success_url: window.location.origin
      });
      
      if (res.data.checkout_url) {
        // Redirect to Stripe checkout
        toast.success('Redirigiendo al pago...');
        window.location.href = res.data.checkout_url;
      } else {
        // No Stripe configured - just record the payment
        toast.success('Pago registrado. Pendiente de confirmar.');
        setShowSubscribeDialog(false);
        fetchSubscriptions();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar el pago');
    }
  };

  const handlePayNow = async (sub) => {
    try {
      const res = await axios.post('/packages/subscribe', {
        package_id: sub.package_id,
        student_id: sub.student_id,
        family_wide: sub.family_wide,
        success_url: window.location.origin
      });
      if (res.data.checkout_url) {
        toast.success('Redirigiendo al pago...');
        window.location.href = res.data.checkout_url;
      } else {
        toast.info('Sistema de pago no disponible. Contacte al administrador.');
      }
    } catch (err) {
      toast.error('Error al procesar pago');
    }
  };

  const handleCancel = async (subId) => {
    if (!window.confirm('¿Desea cancelar este pago pendiente?')) return;
    try {
      await axios.delete(`/packages/unsubscribe/${subId}`);
      toast.success('Pago pendiente cancelado');
      fetchSubscriptions();
    } catch (err) {
      toast.error('Error al cancelar');
    }
  };

  const activeSubs = subscriptions.filter(s => s.status === 'active');

  return (
    <div className="space-y-6">
      {/* Payment history */}
      {activeSubs.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Star className="w-5 h-5 text-orange-500" /> Mis Pagos
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {activeSubs.map((sub) => (
              <Card key={sub.id} className="modern-card" data-testid={`my-sub-${sub.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{sub.package_name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {sub.family_wide ? 'Paquete Familiar' : `Estudiante: ${sub.student_name}`}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-sm font-bold text-orange-600">US$ {sub.package_price?.toLocaleString()}</span>
                        <span className="text-xs text-slate-400">/ {FREQ_LABELS[sub.package_frequency] || sub.package_frequency}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={sub.payment_status === 'paid'
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                        : 'bg-amber-100 text-amber-800 border-amber-200'}>
                        {sub.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                      </Badge>
                      {sub.payment_status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => handlePayNow(sub)}
                            className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white h-7 text-xs"
                            data-testid={`pay-now-${sub.id}`}>
                            <CreditCard className="w-3 h-3 mr-1" /> Pagar Ahora
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleCancel(sub.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50 h-7 text-xs"
                            data-testid={`cancel-sub-${sub.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Cancelar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Available Packages */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Package className="w-5 h-5 text-blue-500" /> Paquetes Disponibles
        </h3>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : packages.length === 0 ? (
          <Card className="modern-card">
            <CardContent className="p-10 text-center">
              <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay paquetes disponibles en este momento</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {packages.map((pkg) => (
              <Card key={pkg.id} className="modern-card hover:shadow-lg transition-all"
                data-testid={`available-package-${pkg.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-slate-800 text-base">{pkg.name}</CardTitle>
                  {pkg.description && <p className="text-xs text-slate-500 mt-1">{pkg.description}</p>}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-orange-600">US$ {pkg.price.toLocaleString()}</span>
                    <span className="text-xs text-slate-400">/ {FREQ_LABELS[pkg.frequency] || pkg.frequency}</span>
                  </div>

                  {pkg.items && pkg.items.length > 0 && (
                    <div className="space-y-1 pb-2">
                      {pkg.items.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                          <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  )}

                  <Button onClick={() => openSubscribe(pkg)}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                    data-testid={`subscribe-pkg-${pkg.id}`}>
                    <CreditCard className="w-4 h-4 mr-2" /> Seleccionar y Pagar
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={showSubscribeDialog} onOpenChange={setShowSubscribeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-orange-500" /> Pagar Paquete
            </DialogTitle>
          </DialogHeader>
          {selectedPackage && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <p className="text-slate-800 font-semibold">{selectedPackage.name}</p>
                <p className="text-orange-600 text-lg font-bold mt-1">
                  US$ {selectedPackage.price.toLocaleString()} / {FREQ_LABELS[selectedPackage.frequency]}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="family_wide"
                    checked={subscribeData.family_wide}
                    onChange={(e) => setSubscribeData({ ...subscribeData, family_wide: e.target.checked, student_id: '' })}
                    className="w-4 h-4 rounded"
                    data-testid="family-wide-checkbox"
                  />
                  <Label htmlFor="family_wide" className="text-slate-600 text-sm cursor-pointer">
                    Paquete Familiar (aplica a todos los hijos)
                  </Label>
                </div>

                {!subscribeData.family_wide && (
                  <div className="space-y-1">
                    <Label className="text-slate-600 text-xs">Seleccionar Estudiante</Label>
                    <Select value={subscribeData.student_id}
                      onValueChange={(val) => setSubscribeData({ ...subscribeData, student_id: val })}>
                      <SelectTrigger data-testid="select-student-for-package">
                        <SelectValue placeholder="Seleccione un hijo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {sortedChildren.map((child) => (
                          <SelectItem key={child.id} value={child.id}>
                            {child.assigned_grade}: {child.first_name} {child.last_name_paternal}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSubscribeDialog(false)}>Cancelar</Button>
                <Button onClick={handleSubscribe}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white"
                  data-testid="confirm-subscribe-btn">
                  Confirmar y Pagar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentPackages;
